import React from 'react';
import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { useHydrationFriendlyAsPath } from 'ap-admin-components/lib/Utilities/Hooks/useHydrationFriendlyAsPath';

export type IActiveLinkProps = LinkProps & {
  children: React.ReactElement;
  activeClassName?: string;
  exact?: boolean;
};

const ActiveLink: React.FC<IActiveLinkProps> = ({ children, activeClassName = 'active', exact, ...props }) => {
  const { isReady } = useRouter();
  const asPath = useHydrationFriendlyAsPath();
  const child = React.Children.only(children);
  const childClassName = child.props.className || '';
  const [className, setClassName] = React.useState(childClassName);

  React.useEffect(() => {
    // Check if the router fields are updated client-side
    if (isReady) {
      // Dynamic route will be matched via props.as
      // Static route will be matched via props.href
      const linkPathname = new URL((props.as || props.href) as string, location.href).pathname;

      // Using URL().pathname to get rid of query and hash
      const activePathname = new URL(asPath, location.href).pathname;

      const isMatch = exact ? activePathname === linkPathname : activePathname.startsWith(linkPathname);
      const newClassName = isMatch ? `${childClassName} ${activeClassName}`.trim() : childClassName;

      if (newClassName !== className) {
        setClassName(newClassName);
      }
    }
  }, [asPath, isReady, props.as, props.href, childClassName, activeClassName, setClassName, className, exact]);
  return (
    <Link
      {...props}
      legacyBehavior={true}
    >
      {React.cloneElement(child, {
        className: className || null,
      })}
    </Link>
  );
};

export default ActiveLink;
